/* eslint-env browser */
'use strict';
import $ from 'jquery';
import _ from 'underscore';

/**
 * The postcard image preview controller for the postcard app.
 */
class PostcardPreview {
  /**
   * Constructor function
   * @constructor
   * @param {HTMLElement} el - component element
   */
  constructor(el) {
    /** @prop {HTMLElement} Component element that contains preview images. */
    this.el = el;

    /** @prop {jQuery} Component element in a jQuery wrapper. */
    this.$el = $(el);

    /** @private {object} Postcard image data. */
    this._data = {
      front: {
        'bg-image': '0',
        'city': '',
        'firstname': '',
        'postcard-message-1': ''
      },
      back: {
        'postcard-message-2': ''
      }
    };
  }

  /**
   * Iterates over the values in a passed `data` object and compares it to
   * `this._data`. If there is a change, `this._data` is updated and an
   * `updates` object is updated to indicate whether the front, back, or both
   * have been updated so that we only trigger an image update as necessary.
   * @method
   * @param {object} data - Data object passed from the postcard.update event.
   * @return {this} PostcardPreview
   */
  update(data) {
    const updates = {};
    const sides = [
      PostcardPreview.Side.FRONT,
      PostcardPreview.Side.BACK
    ];

    _.each(sides, side => {
      for (let key in data) {
        if (key in this._data[side] && this._data[side][key] !== data[key]) {
          this._data[side][key] = data[key];
          updates[side] = true;
        }
      }
    });

    this._updateImages(updates);
    return this;
  }

  /**
   * Receives an `updates` object that indicates which sides should be updated
   * (back and front). For each side that needs to be updated, pre-loads the
   * image and updates the DOM only when the image has finished loading, adding
   * a `loading` class in the interim.
   * @param {object} updates - an object whose keys are back/front, and values
   *   are booleans.
   * @private
   * @return {this} PostcardPreview
   */
  _updateImages(updates) {
    _.each(updates, (updated, side) => {
      if (updated) {
        const $el = this.$el;
        const $renderTarget =
            $el.find(`.postcard-${side}`).find('.postcard-image');
        const img = new Image();
        $renderTarget.addClass(PostcardPreview.CssClass.LOADING);
        img.onload = function() {
          $renderTarget.empty().append(img)
              .removeClass(PostcardPreview.CssClass.LOADING);
          $el.trigger('updated');
        };
        img.src = PostcardPreview.getImageUrl(side, this._data[side]);
      }
    });

    return this;
  }
}

/**
 * Returns the image path for the postcard based on postcard side and data.
 * @function
 * @param {string} side - `front` or `back`
 * @param {object} data - A data object to apply to the image.
 * @return {string} The generated image URL.
 */
PostcardPreview.getImageUrl = function(side, data) {
  let dynamiteParams;
  let imageName;
  if (side === PostcardPreview.Side.FRONT) {
    imageName = `100-rc-postcard-bg-${data['bg-image']}.jpg`;
    dynamiteParams = _.extend(PostcardPreview.FRONT_SETTINGS, {
      'text[0]': data['postcard-message-1'] || '',
      'text[1]': data.firstname || '',
      'text[2]': data.city || ''
    });
  } else {
    imageName = '100-rc-postcard-back.jpg';
    dynamiteParams = _.extend(PostcardPreview.BACK_SETTINGS, {
      text: data['postcard-message-2'] || ''
    });
  }

  return `https://dynamite-images.appspot.com/` +
      `${imageName}?${$.param(dynamiteParams)}`;
};

/**
 * Constant settings for the postcard front.
 * @enum {string}
 */
PostcardPreview.FRONT_SETTINGS = {
  'top[0]': '150',
  'color[0]': '#ffffff',
  'text-align[0]': 'center',
  'white-space[0]': 'normal',
  'max-width[0]': '500',
  'top[1]': '280',
  'font-family[1]': 'OpenSans-Bold',
  'color[1]': '#ffffff',
  'text-align[1]': 'center',
  'max-width[1]': '500',
  'top[2]': '320',
  'font-family[2]': 'OpenSans-Bold',
  'color[2]': '#ffffff',
  'text-align[2]': 'center',
  'white-space[2]': 'normal',
  'max-width[2]': '500'
};

/**
 * Constant settings for the postcard back.
 * @enum {string}
 */
PostcardPreview.BACK_SETTINGS = {
  'left': '30',
  'top': '50',
  'font-size': '22',
  'white-space': 'normal',
  'line-height': '1.8',
  'max-width': '330'
};

/**
 * CSS classes used by this component.
 * @enum {string}
 */
PostcardPreview.CssClass = {
  LOADING: 'loading'
};

/**
 * Postcard sides.
 * @enum {string}
 */
PostcardPreview.Side = {
  BACK: 'back',
  FRONT: 'front'
};

export default PostcardPreview;
